import { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { useRouter } from 'expo-router';

import { canDeleteFinanceImportBatch } from '@couplespace/core/finance/import';

import { Button, Text } from '@/components/ui';
import {
  cancelImportBatch,
  deleteImportBatch,
  fetchImportBatches,
  reparseImportBatch,
  rollbackImportBatch,
  type FinanceImportBatch,
} from '@/lib/finance/smart-import';

function formatWhen(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

export function ImportHistory() {
  const router = useRouter();
  const [batches, setBatches] = useState<FinanceImportBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setBatches(await fetchImportBatches());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load imports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function onRollback(id: string) {
    setBusyId(id);
    try {
      await rollbackImportBatch(id);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rollback failed');
    } finally {
      setBusyId(null);
    }
  }

  async function onCancel(id: string) {
    setBusyId(id);
    try {
      await cancelImportBatch(id);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Cancel failed');
    } finally {
      setBusyId(null);
    }
  }

  async function onDelete(id: string) {
    if (!window.confirm('Remove this import from history? This does not change the ledger.')) {
      return;
    }
    setBusyId(id);
    try {
      await deleteImportBatch(id);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setBusyId(null);
    }
  }

  async function onReparse(id: string) {
    setBusyId(id);
    try {
      const batch = await reparseImportBatch(id);
      router.push(`/finance/transactions/smart-import/${batch.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Re-parse failed');
    } finally {
      setBusyId(null);
    }
  }

  if (loading) {
    return (
      <View className="items-center py-12">
        <ActivityIndicator />
      </View>
    );
  }

  return (
    <View className="gap-4">
      <View className="flex-row items-center gap-3">
        <Text variant="h3">Import history</Text>
        <View className="flex-1" />
        <Button
          label="Back to ledger"
          size="sm"
          onPress={() => router.push('/finance/transactions')}
        />
      </View>

      {error ? <Text className="text-danger">{error}</Text> : null}

      <table className="w-full table-fixed border-collapse text-sm">
        <colgroup>
          <col className="w-[32%]" />
          <col className="w-[12%]" />
          <col className="w-[20%]" />
          <col className="w-[18%]" />
          <col />
        </colgroup>
        <thead>
          <tr className="bg-surface-raised text-left text-xs text-muted">
            <th className="border-b border-border px-2 py-2">Source</th>
            <th className="border-b border-border px-2 py-2">Status</th>
            <th className="border-b border-border px-2 py-2">Created</th>
            <th className="border-b border-border px-2 py-2">Stats</th>
            <th className="border-b border-border px-2 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {batches.length === 0 ? (
            <tr>
              <td colSpan={5} className="px-2 py-6 text-muted">
                No imports yet.
              </td>
            </tr>
          ) : (
            batches.map((batch) => {
              const label = batch.sourceLabel || (batch.filename ?? 'Import');
              const stats = batch.stats ?? {};
              return (
                <tr key={batch.id}>
                  <td className="border-b border-border px-2 py-2 align-middle">
                    <Text className="block truncate">{label}</Text>
                  </td>
                  <td className="border-b border-border px-2 py-2 align-middle whitespace-nowrap">
                    {batch.status}
                  </td>
                  <td className="border-b border-border px-2 py-2 align-middle whitespace-nowrap text-muted">
                    {formatWhen(batch.createdAt)}
                  </td>
                  <td className="border-b border-border px-2 py-2 align-middle whitespace-nowrap">
                    {stats.totalRows ?? 0} rows
                    {(stats.importedRows ?? 0) > 0 ? ` · ${stats.importedRows} imported` : ''}
                    {(stats.linkedRows ?? 0) > 0 ? ` · ${stats.linkedRows} linked` : ''}
                    {(stats.duplicateRows ?? 0) > 0 ? ` · ${stats.duplicateRows} dupes` : ''}
                    {stats.editedSinceImport ? ` · ${stats.editedSinceImport} edited` : ''}
                  </td>
                  <td className="border-b border-border px-2 py-2 align-middle whitespace-nowrap">
                    <View className="flex-row flex-wrap gap-2">
                      {batch.status === 'pending' || batch.status === 'parsing' ? (
                        <>
                          <Button
                            label="Open"
                            size="sm"
                            onPress={() =>
                              router.push(`/finance/transactions/smart-import/${batch.id}`)
                            }
                          />
                          <Button
                            label="Cancel"
                            size="sm"
                            variant="danger"
                            disabled={busyId === batch.id}
                            onPress={() => void onCancel(batch.id)}
                          />
                        </>
                      ) : null}
                      {batch.status === 'ready' ? (
                        <Button
                          label="Review"
                          size="sm"
                          onPress={() =>
                            router.push(`/finance/transactions/smart-import/${batch.id}`)
                          }
                        />
                      ) : null}
                      {batch.status === 'committed' ? (
                        <Button
                          label="Rollback"
                          size="sm"
                          variant="danger"
                          disabled={busyId === batch.id}
                          onPress={() => void onRollback(batch.id)}
                        />
                      ) : null}
                      {batch.status !== 'committed' &&
                      batch.status !== 'pending' &&
                      batch.status !== 'parsing' ? (
                        <Button
                          label="Re-parse"
                          size="sm"
                          variant="secondary"
                          disabled={busyId === batch.id}
                          onPress={() => void onReparse(batch.id)}
                        />
                      ) : null}
                      {canDeleteFinanceImportBatch(batch.status) ? (
                        <Button
                          label="Delete"
                          size="sm"
                          variant="danger"
                          disabled={busyId === batch.id}
                          onPress={() => void onDelete(batch.id)}
                        />
                      ) : null}
                    </View>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </View>
  );
}
